import { ReactNode } from 'react';
import { Items, Item } from './Items';
import { useProducts } from './useProducts';
import { FilterState } from './types';
import { useProductPickerReducer } from './reducer';
import { EmbeddedProduct } from 'modules/supplier/products';
import { hr } from './Items.css';
import { Alert } from '@ff-it/ui';
import equal from 'fast-deep-equal';
import * as style from './index.css';

type ProductPickerProps = {
  filter: FilterState;
  product: EmbeddedProduct | null;
  selectProduct: (product: EmbeddedProduct) => void;
  outlet_or_service_category?: number;
};

export function ProductPicker({
  filter,
  product,
  selectProduct,
  outlet_or_service_category,
}: ProductPickerProps): ReactNode {
  const allProducts = useProducts(filter, outlet_or_service_category);
  const [{ allProducts: currentProducts, vendors, serviceCategories, outlets, products, selection }, dispatch] =
    useProductPickerReducer();

  if (!equal(allProducts, currentProducts)) {
    dispatch({ type: 'SET_PRODUCTS', allProducts });
  }

  if (allProducts.length === 0) {
    return (
      <div className={style.container}>
        <div className={style.column}>
          <Alert className="m-3">Nothing found</Alert>
        </div>
      </div>
    );
  }

  return (
    <div className={style.container}>
      <div className={style.column}>
        {vendors.length > 0 && (
          <Items label="Vendor">
            {vendors.map(({ id, name }) => (
              <Item
                selected={selection?.kind_ === 'vendor' && selection.vendor === id}
                onSelect={() => dispatch({ type: 'SELECT_VENDOR', id })}
                key={id}
                testId={`vendor-${id}`}
              >
                {name}
              </Item>
            ))}
          </Items>
        )}
        {vendors.length > 0 && serviceCategories.length > 0 ? <hr className={hr} /> : null}
        {serviceCategories.length > 0 && (
          <Items label="Service category">
            {serviceCategories.map(({ id, name }) => (
              <Item
                selected={selection?.kind_ === 'service' && selection.service_category === id}
                onSelect={() => dispatch({ type: 'SELECT_SERVICE_CATEGORY', id })}
                key={id}
                testId={`service-category-${id}`}
              >
                {name}
              </Item>
            ))}
          </Items>
        )}
      </div>
      {outlets.length > 0 && (
        <div className={style.column}>
          <Items label="Outlet">
            {outlets.map(({ id, name }) => (
              <Item
                selected={selection?.kind_ === 'vendor' && selection.outlet === id}
                key={id}
                onSelect={() => dispatch({ type: 'SELECT_OUTLET', id })}
                testId={`outlet-${id}`}
              >
                {name}
              </Item>
            ))}
          </Items>
        </div>
      )}
      <div className={style.column}>
        <Items label="Product">
          {products.map((p) => (
            <Item
              selected={product?.id === p.id}
              key={p.id}
              onSelect={() => selectProduct(p)}
              className="product"
              testId={`product-${p.id}`}
            >
              {p.name || 'Generic product'}
            </Item>
          ))}
        </Items>
      </div>
    </div>
  );
}
